import {Object3D, Vector3} from "three";
import {IProjectSettings} from "config/IProjectSettings";
import {Config} from "config/Config";
import {BrowserUtils} from "utils/BrowserUtils";
import {Scene} from "scene/Scene";
import {ContentView} from "view/ContentView";
import {CheckAddonType, ToolsView} from "view/ToolsView";
import {Authentication} from "auth/Authentication";
import {AuthenticationView} from "auth/AuthenticationView";
import {SharingManager} from "sharing/SharingManager";
import {THREEUtils} from "utils/THREEUtils";
import {XHRLoader} from "utils/XHRLoader";
import {PhotoGenerator} from "utils/PhotoGenerator";
import {AddonManager} from "scene/addon/AddonManager";
import {Building} from "building/Building";
import {ResizeManager} from "utils/ResizeManager";
import {AssetManager} from "assets/AssetManager";
import {HTMLUtils} from "utils/HTMLUtils";
import {Signal} from "utils/signal/Signal";
import {BooleanInput} from "utils/ui/BooleanInput";
import {Project} from "auth/Project";
import {ISignalP1} from "./utils/signal/ISignalP1";
import {SceneExporter} from "utils/SceneExporter";
import {CustomPopupWindow} from "ui/CustomPopupWindow";
import {StartTemplates} from "ui/StartTemplates";

interface IVillageConfig
{
	projectSettings: IProjectSettings;
	name: string;
	link: string;
}
export class Main
{
	public render: boolean = true;

	private _config: Config;
	private _scene: Scene;
	private _content: ContentView;
	private _tools: ToolsView;
	private _auth: Authentication;
	private _authView: AuthenticationView;
	private _sharingManager: SharingManager;
	private _viewerMode: boolean = false;
	private _villageMode: boolean = false;
	private _initLayout: boolean = true;

	private _villageConfigs: IVillageConfig[];
	private _villageBuildings: Object3D;
	private _buildingCount: number = 0;
	private _distanceBetweenBuildings: number = 100;
	public currentBuildingID: number = 0;

	private _sideBarToggler: HTMLDivElement = document.getElementById("sideBarToggler") as HTMLDivElement;
	private _sideBar: HTMLDivElement = document.getElementById("properties") as HTMLDivElement;
	private _topBarToggler: HTMLDivElement = document.getElementById("topBarToggler") as HTMLDivElement;
	private _topBar: HTMLDivElement = document.getElementById("topBar") as HTMLDivElement;
	private _canvas: HTMLCanvasElement = document.getElementById("contentCanvas") as HTMLCanvasElement;

	public signals: {
		[key: string]: ISignalP1<null>
	} = {
		assetsLoaded: Signal.create<null>()
	};

	// the query params can change when a user select a project
	// the initial ones remain fixed
	private _initialQueryParams = BrowserUtils.getQueryParams();

	private static _instance: Main;

	private readonly _screenWidthThreshold = 1024;

	public static getInstance(): Main
	{
		return Main._instance || (Main._instance = new Main());
	}

	constructor()
	{
		document.getElementById("footer").innerHTML = `&copy; Mueller, Inc. 2015-${`${new Date().getFullYear()}`.substring(2)}`;
		this._topBarToggler.addEventListener("click", this.toggleTopBar);
		this._sideBarToggler.addEventListener("click", this.toggleSideBar);

		document.getElementById("sharePanelToggler").addEventListener("click", this.toggleSharePanel);

		if (THREEUtils.IsWebGLSupported())
		{
			// WebGL supported -> start the app

			XHRLoader.load({
				method: XHRLoader.METHOD_GET,
				url: "config.json",
				onComplete: this.onConfigLoaded
			});
		}
		else
		{
			// WebGL not supported -> display error message

			document.getElementById("content").classList.add("unsupported");
		}
	}

	private toggleTopBar = () =>
	{
		this._topBarToggler.classList.remove("bounceY");
		this._sideBarToggler.classList.remove("bounceX");
		const className = "collapsed";
		this._topBar.classList.toggle(className);
		if (!this._topBar.classList.contains(className))
		{
			this._sideBar.classList.add(className);
			this._sideBarToggler.classList.remove("open");
			this._topBarToggler.classList.add("open");
			this._topBar.style.marginTop = `0`;
			this._topBar.classList.remove("leftShifted");
		}
		else
		{
			this._topBarToggler.classList.remove("open");
			this._topBar.style.marginTop = `-${HTMLUtils.getSize(this._topBar).height}px`;
		}
		setTimeout(() =>
		{
			this.onResize();
		}, 250);
	};

	private toggleSideBar = () =>
	{
		this._topBarToggler.classList.remove("bounceY");
		this._sideBarToggler.classList.remove("bounceX");
		const className = "collapsed";
		this._sideBar.classList.toggle(className);
		if (!this._sideBar.classList.contains(className))
		{
			this._topBar.classList.add(className);
			this._topBar.style.marginTop = `-${HTMLUtils.getSize(this._topBar).height}px`;
			this._topBar.classList.add("leftShifted");
			this._topBarToggler.classList.remove("open");
			this._sideBarToggler.classList.add("open");
		}
		else
		{
			this._topBar.classList.remove("leftShifted");
			this._sideBarToggler.classList.remove("open");
		}
		setTimeout(() =>
		{
			this.onResize();
		}, 250);
	};

	public collapseSideBar()
	{
		const className = "collapsed";
		this._sideBar.classList.add(className);
		this._topBar.style.marginLeft = ``;
		this._topBarToggler.classList.remove(className);
		this._sideBarToggler.classList.remove("open");
		if (this.isInMobileMode)
		{
			setTimeout(() =>
			{
				this.onResize();
			}, 250);
		}
	}

	private onConfigLoaded = async (xhr: XMLHttpRequest) =>
	{

		var result = JSON.parse(xhr.responseText);
		this._config = new Config(result);
		const config = this._config;

		const assetManager = AssetManager.getInstance();
		this._config.gltfScene = await assetManager.load(config.config.assets.path);
		AddonManager.getInstance(this._config.gltfScene, config.config.assets.addon);
		this.onCoreAssetsLoaded();
	};

	private onCoreAssetsLoaded = async () =>
	{
		this.initPlugins();

		var config = this._config;

		this._scene = new Scene(config);

		var building = new Building(config, this._scene);
		this._scene.building = building;
		//config.building.leanto.addExternalListeners(); // it has to be called here, because in the leantosettings the insetbays were not yet defined.

		this._tools = new ToolsView(this._scene, this._config);
		this._content = new ContentView(config, this._scene, this._tools);
		await this._content.init();
		this._auth = new Authentication();
		this._authView = new AuthenticationView(this._auth);
		this._auth.setView(this._authView);
		this._sharingManager = new SharingManager(this._auth);

		this._tools.createGetAQuoteSection();

		ResizeManager.getInstance().add(this.onResize, true);
		this.onResize();

		// AddonManager listens to this
		//AssetManager.getInstance().addon.load(config.config.assets.addon.path);

		const shadowsToggler = new BooleanInput({label: "Shadows", addLabel: true, vertical: true, id: "toggleShadowsContainer"});
		document.getElementById("content").appendChild(shadowsToggler.element);
		shadowsToggler.changeSignal.add(this._content.toggleShadows);

		if (this._villageMode)
		{
			this._villageBuildings = new Object3D();
			this._villageConfigs = [];
			// copied from the projects of sales@muellerinc.com

			// Rustic Dream
			this._villageConfigs.push({
				name: "Rustic Dream",
				link: 'https://apps.muellerinc.com/design/?email=sales%40muellerinc.com&project=Rustic%20Dream',
				projectSettings: JSON.parse('{"building":{"width":40,"length":80,"height":12,"pitch":4,"overhang_sides":1,"overhang_end":2,"no_walls":false,"ridge_vent_type":"none","ridge_vent_mode":"continuous"},"wainscot":4,"gutter":true,"leanto":{"back":{"enabled":false,"length":15,"pitch":1,"drop":2,"cut_front":0,"cut_back":0},"front":{"enabled":true,"length":10,"pitch":2,"drop":2,"cut_front":0,"cut_back":0},"left":{"enabled":true,"length":10,"pitch":2,"drop":2,"cut_front":0,"cut_back":55},"right":{"enabled":true,"length":10,"pitch":2,"drop":2,"cut_front":0,"cut_back":55},"wraparound_front_left":true,"wraparound_front_right":true,"wraparound_back_left":false,"wraparound_back_right":false,"wall":"off"},"colors":{"wall":"#bbb0a3","roof":"#4e4436","trim":"#4e4436","rollUp":"#d8e5e6","vent":"#d8e5e6","wainscot":"#4e4436"},"addons":[{"itemId":"door_3x7","type":"wall","position":{"wall":"front","horizontal":-2.025,"vertical":0,"x":0,"y":0,"z":0}},{"itemId":"window_6x3","type":"wall","position":{"wall":"front","horizontal":-14.990833333333335,"vertical":5.5005,"x":0,"y":0,"z":0}},{"itemId":"window_3x3","type":"wall","position":{"wall":"front","horizontal":5.482487075414497,"vertical":5.5005,"x":0,"y":0,"z":0}},{"itemId":"window_3x3","type":"wall","position":{"wall":"front","horizontal":13.182970735723455,"vertical":5.5005,"x":0,"y":0,"z":0}},{"itemId":"rollup_10x10","type":"wall","position":{"wall":"left","horizontal":-12.912876302736915,"vertical":0,"x":0,"y":0,"z":0}},{"itemId":"door_3x7","type":"wall","position":{"wall":"left","horizontal":9.917555752632374,"vertical":0,"x":0,"y":0,"z":0}},{"itemId":"window_3x3","type":"wall","position":{"wall":"left","horizontal":31.740361195928276,"vertical":5.591803455668177,"x":0,"y":0,"z":0}},{"itemId":"door_3x7","type":"wall","position":{"wall":"right","horizontal":17.974999999999998,"vertical":0,"x":0,"y":0,"z":0}}],"accessories":[{"id":"truck","enabled":false,"modelPath":"","rotation":[0,0,-10],"position":[-33.18731553775223,0,-8.873617409693793]},{"id":"person","enabled":false,"modelPath":"","rotation":[0,120,0],"position":[20.703164230626584,0,-10.876741660713048]}]}')
			});
			// A Place for Everything
			this._villageConfigs.push({
				name: "A Place for Everything",
				link: 'https://apps.muellerinc.com/design/?email=sales%40muellerinc.com&project=A%20Place%20for%20Everything',
				projectSettings: JSON.parse('{"building":{"width":30,"length":40,"height":15,"pitch":3,"overhang_sides":0,"overhang_end":0,"no_walls":false,"ridge_vent_type":"low","ridge_vent_mode":"continuous"},"wainscot":0,"gutter":true,"leanto":{"back":{"enabled":false,"length":15,"pitch":1,"drop":2,"cut_front":0,"cut_back":0},"front":{"enabled":false,"length":15,"pitch":1,"drop":2,"cut_front":0,"cut_back":0},"left":{"enabled":true,"length":15,"pitch":3,"drop":0,"cut_front":0,"cut_back":0},"right":{"enabled":true,"length":15,"pitch":3,"drop":0,"cut_front":0,"cut_back":0},"wraparound_front_left":false,"wraparound_front_right":false,"wraparound_back_left":false,"wraparound_back_right":false,"wall":"on"},"colors":{"wall":"#baa273","roof":"#376351","trim":"#376351","rollUp":"#7c6a55","vent":"#376351","wainscot":"#d8e5e6"},"addons":[{"itemId":"door_3x7","type":"wall","position":{"wall":"front","horizontal":7.111393507103978,"vertical":0,"x":0,"y":0,"z":0}},{"itemId":"window_3x3","type":"wall","position":{"wall":"front","horizontal":11.490833333333333,"vertical":5.5005,"x":0,"y":0,"z":0}},{"itemId":"rollup_12x10","type":"wall","position":{"wall":"front","horizontal":-7,"vertical":0,"x":0,"y":0,"z":0}}],"accessories":[{"id":"truck","enabled":false,"modelPath":"","rotation":[0,0,-10],"position":[22,0,0]},{"id":"person","enabled":false,"modelPath":"","rotation":[0,120,0],"position":[14,0,40]}]}')
			});
			// A View To Remember
			this._villageConfigs.push({
				name: "A View To Remember",
				link: 'https://apps.muellerinc.com/design/?email=sales%40muellerinc.com&project=A%20View%20To%20Remember',
				projectSettings: JSON.parse('{"building":{"width":40,"length":60,"height":14,"pitch":2,"overhang_sides":0,"overhang_end":0,"no_walls":false,"ridge_vent_type":"none","ridge_vent_mode":"continuous"},"wainscot":0,"gutter":true,"leanto":{"back":{"enabled":true,"length":15,"pitch":1,"drop":0,"cut_front":0,"cut_back":0},"front":{"enabled":false,"length":15,"pitch":4,"drop":2,"cut_front":5,"cut_back":9},"left":{"enabled":false,"length":15,"pitch":1,"drop":2,"cut_front":0,"cut_back":0},"right":{"enabled":false,"length":15,"pitch":1,"drop":2,"cut_front":0,"cut_back":0},"wraparound_front_left":false,"wraparound_front_right":false,"wraparound_back_left":false,"wraparound_back_right":false,"wall":"on"},"colors":{"wall":"#a4a89c","roof":"#d8e5e6","trim":"#d8e5e6","rollUp":"#d8e5e6","vent":"#d8e5e6","wainscot":"#d8e5e6"},"addons":[{"itemId":"rollup_12x12","type":"wall","position":{"wall":"left","horizontal":22,"vertical":0,"x":0,"y":0,"z":0}},{"itemId":"rollup_12x12","type":"wall","position":{"wall":"left","horizontal":0.08135428143630552,"vertical":0,"x":0,"y":0,"z":0}},{"itemId":"rollup_12x12","type":"wall","position":{"wall":"left","horizontal":-21.990886691634095,"vertical":0,"x":0,"y":0,"z":0}}],"accessories":[{"id":"truck","enabled":false,"modelPath":"","rotation":[0,0,-10],"position":[22,0,0]},{"id":"person","enabled":false,"modelPath":"","rotation":[0,120,0],"position":[14,0,40]}]}')
			});
			// Hobby Shop
			this._villageConfigs.push({
				name: "Hobby Shop",
				link: 'https://apps.muellerinc.com/design/?email=sales%40muellerinc.com&project=Hobby%20Shop',
				projectSettings: JSON.parse('{"building":{"width":24,"length":30,"height":10,"pitch":4,"overhang_sides":0,"overhang_end":0,"no_walls":false,"ridge_vent_type":"none","ridge_vent_mode":"continuous"},"wainscot":0,"gutter":true,"leanto":{"back":{"enabled":false,"length":15,"pitch":1,"drop":2,"cut_front":0,"cut_back":0},"front":{"enabled":false,"length":15,"pitch":1,"drop":2,"cut_front":0,"cut_back":0},"left":{"enabled":false,"length":15,"pitch":1,"drop":2,"cut_front":0,"cut_back":0},"right":{"enabled":false,"length":15,"pitch":1,"drop":2,"cut_front":0,"cut_back":0},"wraparound_front_left":false,"wraparound_front_right":false,"wraparound_back_left":false,"wraparound_back_right":false,"wall":"off"},"colors":{"wall":"#cfcaa8","roof":"#376351","trim":"#cfcaa8","rollUp":"#d8e5e6","vent":"#d8e5e6","wainscot":"#d8e5e6"},"addons":[{"itemId":"rollup_10x8_frame","type":"wall","position":{"wall":"right","horizontal":-7.5006505332589715,"vertical":0,"x":0,"y":0,"z":0}},{"itemId":"rollup_8x8","type":"wall","position":{"wall":"right","horizontal":8.845679157511252,"vertical":0,"x":0,"y":0,"z":0}},{"itemId":"door_4x7","type":"wall","position":{"wall":"front","horizontal":2.6235906560662334,"vertical":0,"x":0,"y":0,"z":0}}],"accessories":[{"id":"truck","enabled":false,"modelPath":"","rotation":[0,0,-10],"position":[22,0,0]},{"id":"person","enabled":false,"modelPath":"","rotation":[0,120,0],"position":[14,0,40]}]}')
			});

			// Lakeside Hideaway
			this._villageConfigs.push({
				name: "Lakeside Hideaway",
				link: 'https://apps.muellerinc.com/design/?email=sales%40muellerinc.com&project=Lakeside%20Hideaway',
				projectSettings: JSON.parse('{"building":{"width":30,"length":50,"height":16,"pitch":3,"overhang_sides":1,"overhang_end":1,"no_walls":false,"single_slope":"none","ridge_vent_type":"none","ridge_vent_mode":"continuous"},"wainscot":0,"gutter":true,"leanto":{"back":{"enabled":true,"length":12,"pitch":1,"drop":2,"cut_front":0,"cut_back":0},"front":{"enabled":false,"length":20,"pitch":4,"drop":2,"cut_front":3,"cut_back":3},"left":{"enabled":true,"length":12,"pitch":1,"drop":2,"cut_front":20,"cut_back":0},"right":{"enabled":true,"length":12,"pitch":1,"drop":2,"cut_front":20,"cut_back":0},"wraparound_front_left":false,"wraparound_front_right":false,"wraparound_back_left":true,"wraparound_back_right":true,"wall":"off"},"colors":{"wall":"#636466","roof":"#a11d21","trim":"#a11d21","rollUp":"#a11d21","vent":"#d8e5e6","wainscot":"#d8e5e6"},"addons":[],"accessories":[{"id":"truck","enabled":false,"modelPath":"","rotation":[0,0,-10],"position":[-1.2414188804162052,0,38.36914843084787]},{"id":"person","enabled":false,"modelPath":"","rotation":[0,120,0],"position":[32.4266338576837,0,36.39361883871318]}]}')
			});

			await this.setProjectSettings(this._villageConfigs[this._buildingCount++].projectSettings);

			document.getElementById("building_changer").style.display = "block";
			document.getElementById("left_arrow").onclick = () =>
			{
				if (this.currentBuildingID <= 0)
				{
					return;
				}

				var newBuildingID = --this.currentBuildingID;
				this._content.lookAtBuilding(newBuildingID);

				var link = document.getElementById('building_link');
				link.onclick = () =>
				{
					window.open(this._villageConfigs[newBuildingID].link, '_blank');
				};

				document.getElementById('building_name').innerHTML = this._villageConfigs[newBuildingID].name;
			};

			document.getElementById("right_arrow").onclick = () =>
			{
				if (this.currentBuildingID >= this._villageConfigs.length - 1)
				{
					return;
				}
				var newBuildingID = ++this.currentBuildingID;
				this._content.lookAtBuilding(newBuildingID);

				var link = document.getElementById('building_link');
				link.onclick = () =>
				{
					window.open(this._villageConfigs[newBuildingID].link, '_blank');
				};

				document.getElementById('building_name').innerHTML = this._villageConfigs[newBuildingID].name;
			}
		}
		else
		{
			document.getElementById("help").addEventListener("click", this.onHelpBtnClick);
			const startTemplates = new StartTemplates();
			// const onboard = new Onboard();
			// await onboard.open();

			// document.getElementById("tooltip").addEventListener("click", async () =>
			// {
			// 	await onboard.open(true);
			// });
		}

		this.initPhotoGenerator();
		this.signals.assetsLoaded.dispatch(null);

		HTMLUtils.removeElementFromDOM(document.getElementById("loaderIcon"));
	};

	public readonly onHelpBtnClick = async () =>
	{
		const width = this.isInMobileMode ? 400 : 460;

		await CustomPopupWindow.open("",
			`<div style="margin-top: -50px; margin-bottom: -50px;">
				<iframe src="help/mueller-3d-help.php" style="margin-bottom: 35px; border:none; margin-left: -40px; width: ${width}px; height: 550px;" title="3D Tool Help"</iframe>
			</div>`, true, "Close"
		);
	};

	private saveProjectImage = (email: string, project: Project) =>
	{
		const image = this._content.getScreenShot(1200, 630);

		return new Promise<boolean>((resolve, reject) =>
		{
			const params = {
				image: image,
				email: email,
				projectName: project.name.replace(/\(QUOTED\)$/g, ""),
				projectDate: project.date
			};

			XHRLoader.load({
				url: Authentication.URL + "save_image_force.php",
				method: XHRLoader.METHOD_POST,
				params: params,
				json: false,
				onComplete: async (xhr) =>
				{
					if (xhr.responseText.trim() === "Success!")
					{
						//await WarningWindow.open("Images are saved successfully!");
						console.log("Image is saved successfully!");
						resolve(true);
					}
					else
					{
						//await WarningWindow.open(`Something went wrong:\n${xhr.responseText}`);
						console.log(`Something went wrong:\n${xhr.responseText}`);
						resolve(false);
					}
				},
				onFail: async (xhr) =>
				{
					//await WarningWindow.open(`Something went wrong:\n${xhr.responseText}`);
					console.log(`Something went wrong:\n${xhr.responseText}`);
					resolve(false);
				}
			});
		});
	};

	public saveSideImages = (projectName: string, projectDate?: string) =>
	{
		// if (!this._auth.loggedIn)
		// {
		// 	await WarningWindow.open("You need to be logged in!");
		// 	return;
		// }
		// if (!this._auth.isSelectedProjectSaved())
		// {
		// 	await WarningWindow.open("You need to save the project first!");
		// 	return;
		// }

		return new Promise<boolean>((resolve, reject) =>
		{
			const offsetFromGround = this._config.building.height.value * 0.8;

			const photoGenerator = new PhotoGenerator(this._content.scene3D);

			const buildingWidth = this._config.building.width.value;
			const buildingLength = this._config.building.length.value;

			const coeff = 1.5;

			const distanceFromRight = (buildingWidth / 2 + buildingLength * coeff);
			const distanceFromFront = (buildingLength / 2 + buildingWidth * coeff);

			const cameraTarget = new Vector3(0, offsetFromGround, 0);

			const cameraPositionFromLeft = new Vector3(-distanceFromRight, offsetFromGround, 0);
			const cameraPositionFromFront = new Vector3(0, offsetFromGround, distanceFromFront);
			const cameraPositionFromRight = new Vector3(distanceFromRight, offsetFromGround, 0);
			const cameraPositionFromBack = new Vector3(0, offsetFromGround, -distanceFromFront);


			photoGenerator.setCameraPosition(cameraPositionFromLeft);
			photoGenerator.setCameraTarget(cameraTarget);
			const imageFromLeft = photoGenerator.getPhoto();
			photoGenerator.setCameraPosition(cameraPositionFromFront);
			photoGenerator.setCameraTarget(cameraTarget);
			const imageFromFront = photoGenerator.getPhoto();
			photoGenerator.setCameraPosition(cameraPositionFromRight);
			photoGenerator.setCameraTarget(cameraTarget);
			const imageFromRight = photoGenerator.getPhoto();
			photoGenerator.setCameraPosition(cameraPositionFromBack);
			photoGenerator.setCameraTarget(cameraTarget);
			const imageFromBack = photoGenerator.getPhoto();

			const params: any = {
				email: this._auth.email,
				projectName: projectName,
				images: JSON.stringify({
					left: imageFromLeft,
					front: imageFromFront,
					right: imageFromRight,
					back: imageFromBack
				})
			};

			if (projectDate)
			{
				params.projectDate = projectDate;
			}

			XHRLoader.load({
				url: Authentication.URL + "save_image_views.php",
				method: XHRLoader.METHOD_POST,
				params: params,
				json: false,
				onComplete: async (xhr) =>
				{
					if (xhr.responseText.trim() === "Success!")
					{
						//await WarningWindow.open("Images are saved successfully!");
						console.log("Images are saved successfully!");
						resolve(true);
					}
					else
					{
						//await WarningWindow.open(`Something went wrong:\n${xhr.responseText}`);
						console.log(`Something went wrong:\n${xhr.responseText}`);
						resolve(false);
					}
				},
				onFail: async (xhr) =>
				{
					//await WarningWindow.open(`Something went wrong:\n${xhr.responseText}`);
					console.log(`Something went wrong:\n${xhr.responseText}`);
					resolve(false);
				}
			});
		});
	};

	private initPhotoGenerator()
	{
		//if (this._initialQueryParams["saveimage"] === "on")
		// {
		// 	const saveSideImagesButton = document.createElement('div');
		// 	saveSideImagesButton.classList.add("button");
		// 	saveSideImagesButton.textContent = "Save images to server";
		// 	saveSideImagesButton.addEventListener("click", this.saveSideImages);

		// 	saveSideImagesButton.style.position = "absolute";
		// 	saveSideImagesButton.style.top = "20px";
		// 	saveSideImagesButton.style.right = "20px";
		// 	document.getElementById("content").appendChild(saveSideImagesButton);
		// }

	}

	public async setProjectSettings(projectSettings: IProjectSettings)
	{
		if (this._villageMode)
		{
			if (projectSettings.accessories[0]) // truck
			{
				projectSettings.accessories[0].enabled = true;
				projectSettings.accessories[0].position = [0, 0, projectSettings.building.length / 2 + 15];
			}
		}

		//this.fillSettingsWithNewOptions(projectSettings);
		this._tools.removeListeners();
		this._scene.destroyAddons();
		this._config.setProjectSettings(projectSettings);
		this._tools.setLastValues();

		await this.onAddonsLoaded();

		// TODO not very nice, fields should listen to config directly
		this._tools.update();
		this._tools.addListeners();

		await this._tools.checkForAddons(["left", "right", "front", "back"], [CheckAddonType.WIDTH, CheckAddonType.LENGTH, CheckAddonType.HEIGHT], true);
	}

	private fillSettingsWithNewOptions(settings)
	{
		// add default settings to new options when the saved config is from the older version
		if (!settings.insetbay)
		{
			var insetbay = {
				front: {
					count: 0,
					drop: 0,
					length_1: 20,
					length_2: 20
				},
				back: {
					count: 0,
					drop: 0,
					length_1: 20,
					length_2: 20
				}
			}
			settings.insetbay = insetbay;
		}

		if (settings.building.overhang_front == null && settings.building.overhang_end != null)
		{
			settings.building.overhang_front = settings.building.overhang_end;
			settings.building.overhang_back = settings.building.overhang_end;
		}
		if (settings.building.overhang_right == null && settings.building.overhang_sides != null)
		{
			settings.building.overhang_right = settings.building.overhang_sides;
			settings.building.overhang_left = settings.building.overhang_sides;
		}
	}

	private onAddonsLoaded = async () =>
	{
		await this._scene.building.setAddons(this._config.projectSettings.addons);
		this._scene.building.insetBaymanager.updateAll(); // there was a bug: when the user loaded a project from url, sometimes the trim between 2 inset bays were shown. This one fixes it.

		this._tools.checkForMaximumHeight();

		if (this._villageMode)
		{
			this._content.building.position.x = (this._buildingCount - 1) * this._distanceBetweenBuildings;

			// add truck in front of every house
			this._content.controller.accessoryView.icons[0].accessory.setRotation(new Vector3(0, Math.PI / 2, 0));

			setTimeout(async () =>
			{
				const villageBuilding = THREEUtils.deepClone(this._content.building);
				this._villageBuildings.add(villageBuilding);
				if (this._buildingCount < this._villageConfigs.length)
				{
					await this.setProjectSettings(this._villageConfigs[this._buildingCount++].projectSettings);
				}
				else
				{
					this._content.scene3D.add(this._villageBuildings);
					var link = document.getElementById('building_link');
					link.onclick = () =>
					{
						window.open(this._villageConfigs[this.currentBuildingID].link, '_blank');
					};
					link.style.display = "block";
					document.getElementById('building_name').innerHTML = this._villageConfigs[this.currentBuildingID].name;
				}
			}, 0);
		}
		else
		{
			var building = this._content.building;

			/*building.traverse((obj: Object3D) =>
			{
				if (obj instanceof Mesh)
				{
					obj.castShadow = true;
				}
			});*/
			this._content.scene3D.add(building);
		}

		const projectInfo = Authentication.getProjectInfoFromURL();

		if (!this._auth.isSelectedProjectInitial() && projectInfo?.email === this._auth.email && !this._auth.selectedProject.isQuoted)
		{
			await this._auth.onSave();
		}
		else if (this._auth.selectedProject.isQuoted)
		{
			await this.saveProjectImage(this._auth.email, this._auth.selectedProject);
			await this.saveSideImages(this._auth.selectedProject.name, this._auth.selectedProject.date);
		}
	};

	private toggleSharePanel = () =>
	{
		const style = document.getElementById("sharePanel").style;
		style.display = style.display === "" ? "block" : "";
	};

	public onResize = () =>
	{
		if (this.isInMobileMode)
		{
			if (this._topBar.classList.contains("collapsed"))
			{
				this._topBar.style.marginTop = `-${HTMLUtils.getSize(this._topBar).height}px`;
			}
		}
		else
		{
			this._topBar.style.marginTop = `0`;
		}

		this._content.resize(0, 0);

		requestAnimationFrame(() =>
		{
			this._content.resize(this._canvas.clientWidth, this._canvas.clientHeight);
		});
	};

	private initPlugins()
	{
		if (this._initialQueryParams["viewer"] === "true")
		{
			this._viewerMode = true;
			document.body.className = "viewer";
		}
		if (this._initialQueryParams["village"] === "true")
		{
			this._villageMode = true;
			this._viewerMode = true;
			document.body.className = "viewer";
		}
		if (this._initialQueryParams["exportEnabled"] === "true")
		{
			const exportBtn = document.getElementById("export");
			exportBtn.classList.remove("hidden");
			exportBtn.addEventListener("click", () =>
			{
				SceneExporter.export(this._content.building);
			});
		}
	}

	public get content()
	{
		return this._content;
	}

	public get config(): Config
	{
		return this._config;
	}

	public get scene(): Scene
	{
		return this._scene;
	}

	public get authView()
	{
		return this._authView;
	}

	public getInitialQueryParams(): any
	{
		return this._initialQueryParams;
	}

	public getAccessoryView()
	{
		return this._content.controller.accessoryView;
	}

	public get authentication()
	{
		return this._auth;
	}

	public get toolsView()
	{
		return this._tools;
	}

	public get distanceBetweenBuildings()
	{
		return this._distanceBetweenBuildings;
	}

	public get villageMode()
	{
		return this._villageMode
	}

	public get buildingCount()
	{
		return this._buildingCount;
	}

	public get sharingManager()
	{
		return this._sharingManager;
	}

	public get isInMobileMode()
	{
		return window.innerWidth < this._screenWidthThreshold;
	}
}

// Workaround for esbuild limitation (js error):
// Static functions are not guaranteed to work here yet (eg.: BrowserUtils.getQueryParams), because they're not initialized
requestAnimationFrame(() =>
{
	window["Main"] = Main;
	window["main"] = Main.getInstance();
});
